import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ErrorState } from '@leanin/ui';
import { RetryButton } from '@/components/states/index';
import { getApiClient } from '@/lib/api-client';
import { fetchMyNetworks } from '@/lib/queries/groups';
import { useMember } from '@/lib/use-session';
import { CreateEventForm } from './-components/CreateEventForm';

type Search = { groupId?: string };

export const Route = createFileRoute('/_authed/events/new/')({
  validateSearch: (raw: Record<string, unknown>): Search => ({
    ...(typeof raw['groupId'] === 'string' && raw['groupId'].length > 0
      ? { groupId: raw['groupId'] }
      : {}),
  }),
  component: CreateEventPage,
});

async function fetchMyLeadingCircleOptions() {
  const client = getApiClient();
  const { data, response } = await client.GET(
    '/api/v1/members/me/circles',
    { params: { query: { pageSize: 50, role: 'lead' } } },
  );
  if (data === undefined) {
    throw new Error(`fetchMyLeadingCircleOptions failed (${response.status})`);
  }
  return data.items.map((c) => ({
    id: c.id,
    label: c.name,
    defaultCalendarId: c.defaultCalendarId ?? null,
  }));
}

async function fetchMyLeadingNetworkOptions() {
  const data = await fetchMyNetworks();
  return data.items
    .filter((n) => n.viewerMembership === 'leader')
    .map((n) => ({
      id: n.id,
      label: n.name,
      defaultCalendarId: (n as { defaultCalendarId?: string | null }).defaultCalendarId ?? null,
    }));
}

async function fetchPlatformCalendars() {
  const client = getApiClient();
  const { data } = await client.GET('/api/v1/calendars/platform');
  return data?.items ?? [];
}

function CreateEventPage() {
  const search = Route.useSearch();
  const me = useMember();
  const isAdmin = me?.platformRole === 'admin';

  const circlesQuery = useQuery({
    queryKey: ['events', 'my-leading-circle-options'] as const,
    queryFn: fetchMyLeadingCircleOptions,
  });
  const networksQuery = useQuery({
    queryKey: ['events', 'my-leading-network-options'] as const,
    queryFn: fetchMyLeadingNetworkOptions,
  });
  const platformQuery = useQuery({
    queryKey: ['events', 'platform-calendars'] as const,
    queryFn: fetchPlatformCalendars,
    enabled: isAdmin,
  });

  if (circlesQuery.isLoading || networksQuery.isLoading) return null;
  if (circlesQuery.isError || networksQuery.isError) {
    return (
      <ErrorState
        title="We couldn't load your groups."
        body="Something on our end didn't respond. Give it a moment and try again."
        primary={<RetryButton />}
      />
    );
  }

  return (
    <CreateEventForm
      circles={circlesQuery.data ?? []}
      networks={networksQuery.data ?? []}
      platformCalendars={platformQuery.data ?? []}
      isAdmin={isAdmin}
      {...(search.groupId !== undefined ? { initialGroupId: search.groupId } : {})}
    />
  );
}
